import { useMediaQuery, useTheme } from '@mui/material';
import { createSelector } from '@reduxjs/toolkit';
import { debounce } from 'lodash';
import React, { useCallback, useContext } from 'react';
import { ChannelFilters, MessageResponse } from 'stream-chat';
import {
  useChannelActionContext,
  useChannelStateContext,
  useChatContext,
} from 'stream-chat-react';

import { RightSidebarToggle } from 'src/components/RightSidebarToggle';
import { PortalConfigContext } from 'src/context';
import { MessagesSidebarContext } from 'src/context/messagesSidebarContext';
import { useTypingStatus } from 'src/features/messages/useTypingStatus';
import history from 'src/history';
import { useContextBar } from 'src/hooks/useContextBar';
import { useAppDispatch, useAppSelector } from 'src/hooks/useStore';
import { useGroupChannelActions } from 'src/legacy/components/Channels/useGroupChannelActions';
import { useMemberDropDownHook } from 'src/legacy/components/Channels/useMemberDropdownHook';
import { RootState } from 'src/store';
import {
  AIAssistantStatus,
  clearStatus,
  setStatus,
} from 'src/store/aiAssistantStatus/slice';
import { selectClientsMap } from 'src/store/clients/types';
import { clearDraft, saveDraft } from 'src/store/drafts/slice';
import { alertSnackbar } from 'src/store/ui/actions';
import { getUserIdFromFullId } from 'src/utils/UserUtils';

import { ChannelContentToolbar } from '../Channels/ChannelContentToolbar';

import { ChatChannelContentWrapper } from './ChatChannelContentWrapper';
import { MessageDetailsActions } from './MessageDetailsActions';
import { QueriedMessagesList } from './QueriedMessagesList';
import { useChannelSummaryData } from './useChannelSummaryData';
import { useChatClient } from './useChatClient';

const selector = createSelector(
  [
    (state: RootState) => state.ui.searchValue,
    (state: RootState) => state.user.isClient,
    (state: RootState) => state.messages.deletedChannels,
    (state: RootState) => state.draft.messages,
  ],
  (searchKey, isClient, deletedChannels, messageDraftState) => ({
    searchKey,
    isClient,
    deletedChannels,
    messageDraftState,
  }),
);

export const ChatChannelContent: React.FC<{
  showChannelToolbar?: boolean;
  onDeleteChannel?: () => void;
  placeholderText?: string;
}> = React.memo((props) => {
  const { showChannelToolbar = true, onDeleteChannel, placeholderText } = props;
  const activeClients = useAppSelector(selectClientsMap);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'));
  const chatClient = useChatClient();
  const dispatch = useAppDispatch();
  const { setActiveChannel } = useChatContext();
  const { jumpToMessage } = useChannelActionContext();
  const { open, setOpen } = useContextBar();

  const { visible: hasSidebar } = useContext(MessagesSidebarContext);

  const { searchKey, isClient, deletedChannels, messageDraftState } =
    useAppSelector(selector);

  // Get status data using hooks
  // Note: aiAssistantStatus is retrieved inside ChannelContentWrapper to ensure it's within Channel context
  const typingStatus = useTypingStatus();

  // Callback functions for AI assistant status management
  const handleSetAIAssistantStatus = useCallback(
    (
      channelId: string,
      status: string,
      metadata?: { tool?: string; query?: string },
      statusMessageId?: string,
    ) => {
      dispatch(
        setStatus({
          channelId,
          status: status as AIAssistantStatus,
          metadata,
          messageId: statusMessageId,
        }),
      );
    },
    [dispatch],
  );

  const availableChatChannels = React.useMemo(
    () => (chatClient ? Object.values(chatClient.activeChannels) : []),
    [chatClient],
  );

  const { channel: currentChannel, members } = useChannelStateContext();

  const handleClearAIAssistantStatus = useCallback(() => {
    if (currentChannel?.cid) {
      dispatch(clearStatus({ channelId: currentChannel.cid }));
    }
  }, [dispatch, currentChannel?.cid]);
  const { channelTitle, membersProfiles } =
    useChannelSummaryData(currentChannel);
  const { portalHeader: portalId } = React.useContext(PortalConfigContext);

  const [messageSearchQuery, setMessageSearchQuery] = React.useState('');
  const [queriedMessages, setQueriedMessages] = React.useState<
    MessageResponse[]
  >([]);
  const [isQueriedMessagesLoading, setIsQueriedMessagesLoading] =
    React.useState(false);
  const toolbarRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    setIsQueriedMessagesLoading(true);
    const doubouncedFunc = debounce(async () => {
      if (messageSearchQuery && currentChannel.id) {
        const channelFilters: ChannelFilters = {
          cid: `messaging:${currentChannel.id}`,
        };

        const messageFilters = {
          text: { $autocomplete: messageSearchQuery },
        };
        const searchResponse = await chatClient?.search(
          channelFilters,
          messageFilters,
          {
            sort: [{ relevance: -1 }, { updated_at: -1 }, { created_at: -1 }],
            limit: 10,
          },
        );
        const resultsMessage =
          searchResponse?.results.map((x) => x.message) ?? [];
        setQueriedMessages(resultsMessage);
        setIsQueriedMessagesLoading(false);
      }
    }, 500);

    doubouncedFunc();

    return () => {
      // cancel the debounce function when the component unmounts
      doubouncedFunc.cancel();
    };
  }, [messageSearchQuery]);

  // this is a list of members that do NOT have messaging channels BESIDES than the currently selected one
  const selectedChannelMembersWithOneChannel = React.useMemo(() => {
    const membersMap = members
      ? Object.keys(members).reduce(
          (
            memberMap: Record<string, boolean | undefined>,
            memberID: string,
          ) => {
            memberMap[memberID] = true;
            return memberMap;
          },
          {},
        )
      : {};
    Object.values(availableChatChannels || {}).forEach((activeChannel) => {
      if (
        activeChannel.cid !== currentChannel.cid &&
        !(deletedChannels || []).includes(activeChannel.cid)
      ) {
        Object.keys(activeChannel?.state?.members || {}).forEach((member) => {
          membersMap[member] = undefined;
        });
      }
    });
    return Object.entries(membersMap)
      .filter((membersData) => membersData.at(1))
      .map((membersData) => getUserIdFromFullId(membersData.at(0) as string));
  }, [currentChannel, members, availableChatChannels]);

  // A channel is considered as group channel when it is not a welcome channel OR it is a company channel
  const isGroupChannel = React.useMemo<boolean>(
    () => currentChannel.data?.membershipType === 'group',
    [currentChannel.data],
  );

  const isCompanyChannel = React.useMemo(
    () => currentChannel.data?.membershipType === 'company',
    [currentChannel.data],
  );

  const channelMemberIds = React.useMemo(() => {
    if (membersProfiles) {
      return membersProfiles.map(({ id }) => id);
    }

    return [];
  }, [membersProfiles, selectedChannelMembersWithOneChannel]);

  const handleGroupChannelMemberUpdate = async (newMemberIds: string[]) => {
    if (newMemberIds.length === 0) {
      return;
    }

    // all member which are present in messaging channel which includes client, staff and admin
    const channelAllMemberIds = Object.keys(currentChannel.state.members).map(
      (id) => id.slice(id.lastIndexOf('_') + 1),
    );
    const allAssignedClients = Array.from(activeClients.keys());
    // only client users from messaging channel members
    const channelMemberClientIds = channelAllMemberIds.filter((id) =>
      allAssignedClients.includes(id),
    );
    // segregate client users which needs to be removed from channel
    const toRemoveClients = channelMemberClientIds.filter(
      (id) => !newMemberIds.includes(id),
    );
    const toRemoveClientsMessagingIds = toRemoveClients.map(
      (memberId) => `${portalId}_${memberId}`,
    );
    // segregate client users which needs to be added to channel
    const toAddClients = newMemberIds.filter(
      (id) => !channelMemberClientIds.includes(id),
    );
    const toAddClientsMessagingIds = toAddClients.map(
      (memberId) => `${portalId}_${memberId}`,
    );

    try {
      if (toAddClientsMessagingIds.length > 0) {
        await currentChannel.addMembers(toAddClientsMessagingIds);
      }
      if (toRemoveClientsMessagingIds.length > 0) {
        await currentChannel.removeMembers(toRemoveClientsMessagingIds);
      }

      if (
        toAddClientsMessagingIds.length > 0 ||
        toRemoveClientsMessagingIds.length > 0
      ) {
        dispatch(
          alertSnackbar({
            successMessage: 'Channel updated',
          }),
        );
      }
    } catch (err) {
      dispatch(
        alertSnackbar({
          successMessage:
            'There was a problem deleting this channel. Support has been notified to investigate the issue.',
        }),
      );
    }
  };

  const {
    setShowGroupMembersDropDown,
    RenderGroupMembersDropdown,
    setGroupMemberDropdownAnchor,
  } = useMemberDropDownHook({
    id: 'messaging-edit-members-dropdown',
    memberIDs: channelMemberIds,
    selectedChannelMembersWithOneChannel,
    onMemberUpdates: handleGroupChannelMemberUpdate,
    companyId:
      typeof currentChannel.data?.companyID === 'string'
        ? currentChannel.data?.companyID
        : '',
  });

  const handleEdit = React.useCallback(() => {
    setShowGroupMembersDropDown(true);
  }, [setShowGroupMembersDropDown]);
  const { groupChannelActions, DeleteConfirmationModal } =
    useGroupChannelActions({
      channelType: 'messaging',
      isGroupChannel,
      enableActions: !isClient,
      onEditClick: handleEdit,
      onDeleteModalSuccess: onDeleteChannel,
      disableDeletion: selectedChannelMembersWithOneChannel.length > 0,
      isCompanyChannel,
    });

  const handleBackClick = () => {
    history.replace('/messaging', {
      search: '',
    });
    setActiveChannel();
  };

  const handleQueriedMessageSelected = (messageId: string) => {
    setMessageSearchQuery('');
    jumpToMessage(messageId);
  };

  const getInitialMessageInputValue = React.useCallback(() => {
    if (messageDraftState && currentChannel.id) {
      const messageDraft = messageDraftState[currentChannel.id];
      if (messageDraft) {
        return messageDraft.message;
      }
    }
    return '';
  }, [messageDraftState, currentChannel.id]);

  const handleUpdateDraft = useCallback(
    debounce((entityId: string, message: string) => {
      if (message !== '') {
        // save draft when message has content
        dispatch(
          saveDraft({
            entityType: 'messages',
            entityId,
            draftValue: {
              message,
            },
          }),
        );
      } else {
        // clear draft when message is empty
        dispatch(
          clearDraft({
            entityType: 'messages',
            entityIds: [entityId],
          }),
        );
      }
    }, 500),
    [dispatch],
  );

  const handleMessageInputChange = useCallback(
    (text: string) => {
      if (currentChannel.id) {
        handleUpdateDraft(currentChannel.id, text ?? '');
      }
    },
    [currentChannel.id, handleUpdateDraft],
  );

  const handleMessageInputSubmit = useCallback(() => {
    // clear draft
    if (currentChannel.id) {
      handleUpdateDraft.cancel();
      dispatch(
        clearDraft({
          entityType: 'messages',
          entityIds: [currentChannel.id],
        }),
      );
    }
  }, [currentChannel.id, handleUpdateDraft, dispatch]);

  return (
    <>
      <ChatChannelContentWrapper
        ChannelToolbar={
          showChannelToolbar ? (
            <ChannelContentToolbar
              ref={toolbarRef}
              title={channelTitle}
              // when back click handler is defined we show the back arrow button
              // on mobile.
              // when it is client experience and channels list is hidden then
              // we do not show the back arrow button.
              onBackClick={
                isClient && !hasSidebar ? undefined : handleBackClick
              }
              isChannelsListShown={hasSidebar}
              AdditionalSlotsComponents={[
                <React.Fragment key="message-details-actions">
                  <MessageDetailsActions
                    messageSearchQuery={messageSearchQuery}
                    actions={groupChannelActions}
                    toolbarElement={toolbarRef.current}
                    setMessageSearchQuery={setMessageSearchQuery}
                    setGroupMemberDropdownAnchor={setGroupMemberDropdownAnchor}
                  />
                  {!isClient && (
                    <RightSidebarToggle
                      className="flex sm:hidden ml-2"
                      isSidebarOpen={open}
                      onToggle={() => setOpen(!open)}
                    />
                  )}
                </React.Fragment>,
              ]}
            />
          ) : undefined
        }
        MessageListOverride={
          messageSearchQuery ? (
            <QueriedMessagesList
              query={messageSearchQuery}
              queriedMessages={queriedMessages}
              isLoading={isQueriedMessagesLoading}
              onMessageClick={handleQueriedMessageSelected}
            />
          ) : undefined
        }
        allowScroll={Boolean(messageSearchQuery)}
        autoFocus={!isMobile && searchKey === ''}
        defaultInputValue={getInitialMessageInputValue()}
        onChange={handleMessageInputChange}
        onSubmit={handleMessageInputSubmit}
        showMessageInput={messageSearchQuery === ''}
        placeholderText={placeholderText}
        typingStatus={typingStatus}
        isInternalChat={false}
        setAIAssistantStatus={handleSetAIAssistantStatus}
        clearAIAssistantStatus={handleClearAIAssistantStatus}
      />
      {DeleteConfirmationModal()}
      {RenderGroupMembersDropdown()}
    </>
  );
});
